import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { logger } from '@/lib/logger'

export interface GraphContainer {
  id: string
  user_id: string
  name: string
  color: string
  background_image_path: string | null
  position_x: number
  position_y: number
  width: number
  height: number
  is_collapsed: boolean
  created_at: string
  updated_at: string
}

export interface CreateContainerData {
  name: string
  color?: string
  background_image_path?: string | null
  position_x?: number
  position_y?: number
  width?: number
  height?: number
}

export const useGraphContainersStore = defineStore('graphContainers', () => {
  const authStore = useAuthStore()
  
  // State
  const containers = ref<Map<string, GraphContainer>>(new Map())
  const isLoading = ref(false)

  // Getters
  const allContainers = computed(() => Array.from(containers.value.values()))
  
  const getContainerById = (id: string): GraphContainer | undefined => {
    return containers.value.get(id)
  }

  // Actions
  const fetchContainers = async (): Promise<void> => {
    if (!authStore.user?.id) return

    isLoading.value = true
    try {
      const provider = getDataProvider()
      const data = await provider.query<GraphContainer>('graph_containers', {
        orderBy: [{ column: 'created_at', ascending: true }]
      })

      containers.value.clear()
      for (const container of data || []) {
        containers.value.set(container.id, container)
      }

      logger.info('📦 Fetched graph containers:', containers.value.size)
    } catch (error) {
      logger.error('Failed to fetch containers:', error)
    } finally {
      isLoading.value = false
    }
  }

  const createContainer = async (data: CreateContainerData): Promise<{ success: boolean; error?: string; data?: GraphContainer }> => {
    if (!authStore.user?.id) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const containerData = {
        user_id: authStore.user.id,
        name: data.name,
        color: data.color || 'transparent',
        position_x: data.position_x || 0,
        position_y: data.position_y || 0,
        width: data.width || 400,
        height: data.height || 300
      }

      const provider = getDataProvider()
      const result = await provider.insert<GraphContainer>('graph_containers', containerData)

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to create container')
      }

      containers.value.set(result.data.id, result.data)
      logger.info('📦 Created container:', result.data.name)
      
      return { success: true, data: result.data }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create container'
      logger.error('Failed to create container:', error)
      return { success: false, error: message }
    }
  }

  const updateContainer = async (id: string, updates: Partial<GraphContainer>): Promise<{ success: boolean; error?: string }> => {
    try {
      const provider = getDataProvider()
      const result = await provider.update('graph_containers', id, {
        ...updates,
        updated_at: new Date().toISOString()
      })

      if (!result.success) {
        throw new Error(result.error)
      }

      const existing = containers.value.get(id)
      if (existing) {
        containers.value.set(id, { ...existing, ...updates })
      }

      return { success: true }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update container'
      logger.error('Failed to update container:', error)
      return { success: false, error: message }
    }
  }

  const updateContainerPosition = async (id: string, x: number, y: number): Promise<void> => {
    await updateContainer(id, { position_x: x, position_y: y })
  }

  const updateContainerSize = async (id: string, width: number, height: number): Promise<void> => {
    await updateContainer(id, { width, height })
  }

  const deleteContainer = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const provider = getDataProvider()
      const result = await provider.delete('graph_containers', id)

      if (!result.success) {
        throw new Error(result.error)
      }

      containers.value.delete(id)
      logger.info('📦 Deleted container:', id)
      
      return { success: true }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete container'
      logger.error('Failed to delete container:', error)
      return { success: false, error: message }
    }
  }

  // Get signed URL for background image
  const getBackgroundImageUrl = async (storagePath: string): Promise<string> => {
    const provider = getDataProvider()
    const url = await provider.getFileUrl('user-images', storagePath, 3600)

    if (!url) {
      logger.error('Failed to create signed URL for container background')
      return ''
    }

    return url
  }

  return {
    // State
    containers,
    isLoading,
    
    // Getters
    allContainers,
    getContainerById,
    
    // Actions
    fetchContainers,
    createContainer,
    updateContainer,
    updateContainerPosition,
    updateContainerSize,
    deleteContainer,
    getBackgroundImageUrl
  }
})
